import * as React from 'react';
import { normaliserAllures } from './seanceEngine.ts';

// Ajustement des recuperations : preference de l'utilisateur (secondes,
// positif ou negatif) appliquee a chaque recuperation EN TEMPS d'une
// notation ("R2'" -> "R1'30''" pour -30). Les recuperations en distance
// ("R400m", "R1km") et les blocs "@RECUP" explicites ne sont pas modifies.
// La notation modele de la seance n'est jamais modifiee : la version
// ajustee sert a l'affichage et aux calculs (duree, charge, RPE).

// Recuperation en temps : R + nombre + unite optionnelle (min, ', '', ", s,
// ou minutes'secondes). Exclut les distances (R400m, R1km) et "RECUP".
const RE_RECUP = /R(\d+(?:[.,]\d+)?)(?:(min)|('')|(")|(s)|'(?:(\d+(?:[.,]\d+)?)(?:''|"|s)?)?)?(?![\d.,]*\s*k?m\b)/gi;

// Recuperation minimale apres ajustement (secondes).
export const RECUP_MIN_S = 15;

function nombre(texte: string): number {
  return parseFloat(texte.replace(',', '.'));
}

/** Duree (secondes) au format de la notation : 90 -> 1'30'', 120 -> 2', 45 -> 45''. */
export function formaterRecuperation(secondes: number): string {
  const total = Math.max(0, Math.round(secondes));
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s}''`;
  return s === 0 ? `${m}'` : `${m}'${String(s).padStart(2, '0')}''`;
}

/** Notation dont chaque recuperation en temps est decalee de `deltaS` secondes (minimum 0). */
export function ajusterNotation(notation: string, deltaS: number): string {
  if (!deltaS) return notation;
  return notation.replace(RE_RECUP, (tout, n, min, sec2, sec1, secS, secApresMin) => {
    let secondes: number;
    if (min) secondes = nombre(n) * 60;
    else if (sec2 || sec1 || secS) secondes = nombre(n);
    else if (secApresMin !== undefined) secondes = nombre(n) * 60 + nombre(secApresMin);
    else secondes = nombre(n) * 60; // R2' ou R2 (sans unite = minutes)
    if (isNaN(secondes)) return tout;
    // Plancher : un ajustement negatif ne descend pas sous RECUP_MIN_S (sauf
    // si la recuperation prevue etait deja plus courte).
    return 'R' + formaterRecuperation(Math.max(Math.min(RECUP_MIN_S, secondes), secondes + deltaS));
  });
}

/** Notation affichee : recuperations ajustees et codes d'allure canoniques ("@10k" -> "@10K"). */
export function afficherNotation(notation: string, deltaS: number): string {
  return normaliserAllures(ajusterNotation(notation, deltaS));
}

/** Libelle de l'ajustement : "+30 s", "−30 s", "aucun". */
export function libelleAjustement(deltaS: number): string {
  if (!deltaS) return 'aucun';
  return `${deltaS > 0 ? '+' : '−'}${Math.abs(deltaS)} s`;
}

const CLE_CACHE = 'ajustementRecup';
export const AJUSTEMENT_RECUP_MODIFIE_EVENT = 'app:ajustement-recup-modifie';

function lireCache(): number {
  try {
    const v = parseInt(sessionStorage.getItem(CLE_CACHE) ?? '', 10);
    return isNaN(v) ? 0 : v;
  } catch {
    return 0;
  }
}

function entetes(): HeadersInit {
  return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + sessionStorage.getItem('token') };
}

function url(athlete: string | null): string {
  return '/api/preference/ajustement-recup' + (athlete ? '?athlete=' + encodeURIComponent(athlete) : '');
}

/**
 * Ajustement des recuperations de l'utilisateur (GET /api/preference/ajustement-recup),
 * ou d'un athlete rattache consulte par son coach (athlete, sans cache).
 */
export function useAjustementRecup(athlete: string | null = null): number {
  const [delta, setDelta] = React.useState<number>(athlete ? 0 : lireCache());
  React.useEffect(() => {
    const charger = () =>
      fetch(url(athlete), { headers: entetes() })
        .then(async (r) => {
          if (r.status !== 200) return;
          const v = (await r.json()) as number;
          setDelta(v);
          if (!athlete) {
            try { sessionStorage.setItem(CLE_CACHE, String(v)); } catch { /* ignore */ }
          }
        })
        .catch(() => { /* on garde la valeur en cache */ });
    charger();
    window.addEventListener(AJUSTEMENT_RECUP_MODIFIE_EVENT, charger);
    return () => window.removeEventListener(AJUSTEMENT_RECUP_MODIFIE_EVENT, charger);
  }, [athlete]);
  return delta;
}

/**
 * Enregistre l'ajustement (PUT) et previent les ecrans qui l'utilisent.
 * Reserve au coach (le sien, ou celui d'un athlete rattache : athlete).
 */
export async function enregistrerAjustementRecup(deltaS: number, athlete: string | null = null): Promise<number> {
  const r = await fetch(url(athlete), { method: 'PUT', headers: entetes(), body: JSON.stringify(deltaS) });
  if (r.status !== 200) throw new Error('statut ' + r.status);
  const v = (await r.json()) as number;
  if (!athlete) {
    try { sessionStorage.setItem(CLE_CACHE, String(v)); } catch { /* ignore */ }
  }
  window.dispatchEvent(new Event(AJUSTEMENT_RECUP_MODIFIE_EVENT));
  return v;
}
